import { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useSnackBar } from "@/utils/useSnackbar";

import { GenerateBar } from "@/pages/Private/pages/Messages/components/GenerateBar/GenerateBar";
import { GenerateBarBulkSelection } from "@/pages/Private/pages/Messages/components/GenerateBar/useGenerateBarSubmit";

import { isDummyProspect } from "@/pages/Private/pages/List/utils/columns/dummyProspect";
import { useProspectLinkedinConnections } from "@/pages/Private/pages/List/components/CampaignOverview/utils/useProspectLinkedinConnections";

import { reachOutBulkScope, ReachOutTarget } from "./reachOutTarget";

interface ReachOutAiPanelProps {
	target: ReachOutTarget;
	/** Portal target for GenerateBar's own dropdowns/modals (keeps them inside the menu's scope). */
	portalContainer?: HTMLElement | null;
	/** Seed text prefilled into the instruction input (feed run summary / signal line, PREC-3635 P6). */
	triggerDescription?: string | null;
	/** Tracker signal the generation is seeded from (feed per-signal CTA) — maps the job to that row. */
	seedSignalId?: number;
	/** Fired once a full sequence finishes generating (single lead) or a bulk batch is enqueued. */
	onGenerated?: () => void;
}

/**
 * The reach-out menu's inline "Let AI write a sequence" body. Reuses the full {@link GenerateBar}
 * (instruction input, language/sender/channel pickers, LinkedIn invite-note + framework toggles,
 * generate) — driven directly from the target with no chat/inbox selection state.
 *
 * For a single lead the generated sequence persists to `list_person.copywritingOutput`, so the
 * Outreach column's button flips to "Review" on its own. For a bulk selection the same GenerateBar
 * runs in `bulkSelection` mode: "Generate" enqueues a full sequence for every selected lead (current
 * page) or every lead matching the filter (select all across pages), and reports back via `onGenerated`.
 */
export const ReachOutAiPanel: FunctionComponent<ReachOutAiPanelProps> = ({
	target,
	portalContainer,
	triggerDescription,
	seedSignalId,
	onGenerated,
}) => {
	const { t } = useTranslation();
	const { handleOpenSnackbar } = useSnackBar();

	// Single lead → that lead; bulk → a representative seed row (display defaults only; language and
	// senders resolve per lead on the backend).
	const row = target.kind === "lead" ? target.row : target.seedRow;
	const prospect = row.prospect;

	// A message counts as a conversation only if it belongs to this list_person (a prospect can be
	// on several lists); the flag switches GenerateBar between "first touch" and "follow-up" copy.
	const hasConversations = useMemo(
		() => (prospect?.conversations?.filter((c) => c?.listPersonId === row.id).length ?? 0) > 0,
		[prospect?.conversations, row.id]
	);

	// Resolve which senders are already connected to this prospect so GenerateBar can turn the
	// "Add note to LinkedIn invite" toggle off + disabled for a connected sender (matching the
	// Messages/inbox path) — otherwise it defaults on and generates an invite + "thanks for
	// accepting" opener for an already-connected prospect.
	// A sender whose own invitation is pending gets no second invitation note either (PREC-4216).
	const {
		connectedUserIds,
		pendingInvitationUserIds,
		connectionCheckDone,
		lastSenderUserId,
		isLastSenderConnected,
	} = useProspectLinkedinConnections({
		prospectLinkedinUrl: prospect?.linkedinUrl,
		conversations: prospect?.conversations,
		listPersonId: row.id,
	});

	const bulkSelection = useMemo<GenerateBarBulkSelection | undefined>(() => {
		if (target.kind !== "bulk") {
			return undefined;
		}

		return {
			listId: target.listId,
			selectedItems: target.selectedItems,
			count: target.count,
			assignStatus: target.assignStatus,
			// The table's whole view, as every other bulk path sends it (PREC-4450).
			...reachOutBulkScope(target),
			filter: target.filter as Record<string, unknown> | undefined,
		};
	}, [target]);

	if (!prospect?.id) {
		return null;
	}

	return (
		<div className="px-1 pb-1" onClick={(e) => e.stopPropagation()}>
			{/* Non-compact layout (full-label, balanced control row) — the menu is wide enough for it —
			    but `hideBatchMode` keeps the multi-lead "next N leads" toggle off (the bulk selection is
			    the batch here; a single lead has no queue). */}
			<GenerateBar
				hideBatchMode
				smallGenerate
				bulkSelection={bulkSelection}
				seedSignalId={seedSignalId}
				triggerDescription={triggerDescription}
				companyCategory={
					prospect.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory?.category
				}
				connectedUserIds={connectedUserIds}
				connectionCheckDone={connectionCheckDone}
				pendingInvitationUserIds={pendingInvitationUserIds}
				copywritingError={row.copywritingError}
				copywritingOutput={row.copywritingOutput}
				copywritingStatus={row.copywritingStatus}
				hasConversations={hasConversations}
				isLastSenderConnected={isLastSenderConnected}
				lastSenderUserId={lastSenderUserId}
				listId={target.kind === "bulk" ? target.listId : (row.list?.id ?? 0)}
				personaCategory={prospect.scorePersonaCategoryResult?.scorePersonaCategory?.category}
				portalContainer={portalContainer}
				prospectCountry={prospect.country}
				prospectFirstName={prospect.firstName}
				prospectHasEmail={!!prospect.email}
				prospectHasLinkedinUrl={!!prospect.linkedinUrl}
				prospectId={prospect.id}
				prospectLanguage={prospect.language}
				prospectNotResolved={isDummyProspect(row)}
				onBulkGenerated={(result) => {
					handleOpenSnackbar(
						t("list.reachOut.toast.generatingBulk", {
							count: result.accepted.length || (target.kind === "bulk" ? target.count : 0),
						})
					);
					onGenerated?.();
				}}
				onGenerationComplete={() => onGenerated?.()}
			/>
		</div>
	);
};
